/**
 * Permission API Service - Wrapper for generated OpenAPI client
 *
 * This service provides a facade over the generated PermissionsApiService
 * to maintain backward compatibility with the permission.store.ts interface.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, of, map, switchMap, tap, finalize, shareReplay, timeout } from 'rxjs';
import { PermissionsApiService as GeneratedPermissionsService } from '@api/api/permissions.service';
import {
  PermissionResponse,
  RoleResponse,
  UserRoleResponse,
  EffectivePermissionsResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignRoleRequest
} from '@api/model/models';
import type { Permission, Role, UserRole, EffectivePermissions } from '@core/models/permission.model';

export type { Permission, Role, UserRole, EffectivePermissions } from '@core/models/permission.model';

/**
 * Transform API PermissionResponse to local Permission interface
 */
function toPermission(p: PermissionResponse): Permission {
  return {
    id: p.id,
    name: p.name,
    description: p.description ?? '',
    resource: p.resource,
    action: p.action,
    category: p.resource, // Use resource as category fallback
    created_at: '',
    updated_at: ''
  };
}

/**
 * Transform API RoleResponse to local Role interface
 */
function toRole(r: RoleResponse): Role {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? '',
    permissions: (r.permissions ?? []).map(toPermission),
    // `undefined` means hierarchy evidence was not loaded for this response;
    // an empty array from the catalog list is an authoritative root role.
    parent_roles: r.parent_role_ids ?? undefined,
    // Scope remains unavailable; is_system is the catalog truth used to guard
    // protected-role controls.
    scope: null,
    is_system: r.is_system,
    // Real fields exposed by the backend RoleResponse.
    require_mfa: r.require_mfa,
    priority: r.priority,
    created_at: '',
    updated_at: ''
  };
}

/**
 * Transform API UserRoleResponse to local UserRole interface
 */
function toUserRole(ur: UserRoleResponse, roles: Role[]): UserRole {
  const role = roles.find(r => r.id === ur.role_id) ?? {
    id: ur.role_id,
    name: ur.role_name,
    description: '',
    permissions: [],
    // Scope remains unavailable. The system flag comes from the already-loaded
    // role catalog, falling back to the assignment response only if a role was
    // created between the two reads.
    scope: null,
    is_system: ur.is_system,
    created_at: '',
    updated_at: ''
  };

  return {
    user_id: ur.user_id,
    role_id: ur.role_id,
    role,
    // granted_by is null for legacy rows created before the assigned_by column
    // was populated; normalize to '' because the local UserRole type is string.
    granted_by: ur.granted_by ?? '',
    granted_at: ur.assigned_at,
    expires_at: ur.expires_at ?? undefined,
    tenant_id: ur.tenant_id
  };
}

@Injectable({
  providedIn: 'root'
})
export class PermissionApiService {
  private readonly api = inject(GeneratedPermissionsService);
  private cachedRoles: Role[] = [];

  // COLD-LOAD DEDUP (HAR 2026-07-01): on cold load several bootstrap triggers
  // (PermissionStore.onInit loaders, the principal-change effect's
  // getUserRoles→getRoles fallback, and permissions-management refresh) fired
  // getRoles()×3 / getPermissions()×2 concurrently — no single-flight. These
  // share one in-flight request (same shareReplay pattern as
  // AuthService.effectivePermissionsInFlight), cleared on completion.
  private rolesInFlight$: Observable<Role[]> | null = null;
  private permissionsInFlight$: Observable<Permission[]> | null = null;

  // Bound the shared request. MANDATORY with shareReplay({refCount:false}):
  // finalize only clears the in-flight handle on source TERMINATION, so without a
  // timeout a black-holed request (no response, no RST) would never terminate →
  // the handle stays set → every future caller gets the same dead stream. This
  // mirrors AuthService.refreshTokenSingleFlight's inner timeout (the single-flight
  // shape is only hang-safe WITH it). On timeout the source errors → finalize
  // clears → the next call issues a fresh request.
  private readonly REQUEST_TIMEOUT_MS = 15_000;

  /**
   * Clear the cached roles. Call after any role CRUD operation to prevent
   * stale permission data from persisting.
   */
  clearCachedRoles(): void {
    this.cachedRoles = [];
    // Drop any in-flight roles fetch so the next getRoles() re-queries fresh
    // data (post-CRUD the list has changed).
    this.rolesInFlight$ = null;
  }

  /**
   * Get all permissions. Single-flight: concurrent callers share one request.
   */
  getPermissions(): Observable<Permission[]> {
    if (this.permissionsInFlight$) {
      return this.permissionsInFlight$;
    }
    this.permissionsInFlight$ = this.api.listPermissions().pipe(
      map(permissions => permissions.map(toPermission)),
      timeout({ first: this.REQUEST_TIMEOUT_MS }),
      finalize(() => { this.permissionsInFlight$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.permissionsInFlight$;
  }

  /**
   * Get all roles. Single-flight: concurrent callers (incl. the getUserRoles
   * empty-cache fallback below) share one request.
   */
  getRoles(): Observable<Role[]> {
    if (this.rolesInFlight$) {
      return this.rolesInFlight$;
    }
    this.rolesInFlight$ = this.api.listRoles().pipe(
      map(roles => {
        this.cachedRoles = roles.map(toRole);
        return this.cachedRoles;
      }),
      timeout({ first: this.REQUEST_TIMEOUT_MS }),
      finalize(() => { this.rolesInFlight$ = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.rolesInFlight$;
  }

  /**
   * Get user roles, enriched with full role data (including permissions).
   *
   * If roles have not been cached yet (e.g., getUserRoles() called before
   * getRoles() completes), fetches them first to avoid returning stubs
   * with empty permissions arrays.
   */
  getUserRoles(userId: string): Observable<UserRole[]> {
    const ensureRoles$ = this.cachedRoles.length > 0
      ? of(this.cachedRoles)
      : this.getRoles();

    return ensureRoles$.pipe(
      switchMap(roles =>
        this.api.getUserRoles(userId).pipe(
          map(userRoles => userRoles.map((ur: UserRoleResponse) => toUserRole(ur, roles)))
        )
      )
    );
  }

  /**
   * Get effective permissions for a user
   */
  getEffectivePermissions(userId: string): Observable<EffectivePermissions> {
    return this.api.getUserEffectivePermissions(userId).pipe(
      map((resp: EffectivePermissionsResponse) => ({
        permissions: resp.permissions.map(toPermission),
        roles: resp.roles.map(toRole),
        computed_at: resp.computed_at,
        cache_expires_at: new Date(Date.now() + 5 * 60 * 1000).toISOString() // 5 min cache
      }))
    );
  }

  /**
   * Assign role to user, enriched with full role data.
   */
  assignRole(userId: string, roleId: string): Observable<UserRole> {
    const request: AssignRoleRequest = { role_id: roleId };
    const ensureRoles$ = this.cachedRoles.length > 0
      ? of(this.cachedRoles)
      : this.getRoles();

    return ensureRoles$.pipe(
      switchMap(roles =>
        this.api.assignRoleToUser(userId, request).pipe(
          map(ur => toUserRole(ur, roles)),
          tap(() => this.clearCachedRoles())
        )
      )
    );
  }

  /**
   * Remove role from user
   */
  removeRole(userId: string, roleId: string): Observable<void> {
    return this.api.removeRoleFromUser(userId, roleId).pipe(
      map(() => undefined),
      tap(() => this.clearCachedRoles())
    );
  }

  /**
   * Create a new role
   */
  createRole(roleData: Pick<Role, 'name' | 'description' | 'permissions'>): Observable<Role> {
    const request: CreateRoleRequest = {
      name: roleData.name,
      description: roleData.description,
      permission_ids: roleData.permissions.map(p => p.id)
    };
    return this.api.createRole(request).pipe(
      map(toRole),
      tap(() => this.clearCachedRoles())
    );
  }

  /**
   * Update an existing role
   * Note: Permission updates are not currently supported via UpdateRoleRequest
   */
  updateRole(role: Role): Observable<Role> {
    const request: UpdateRoleRequest = {
      name: role.name,
      description: role.description
    };
    return this.api.updateRole(role.id, request).pipe(
      map(toRole),
      tap(() => this.clearCachedRoles())
    );
  }

  /**
   * Delete a role
   */
  deleteRole(roleId: string): Observable<void> {
    return this.api.deleteRole(roleId).pipe(
      map(() => undefined),
      tap(() => this.clearCachedRoles())
    );
  }
}
